
import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { Mail, UserPlus } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useQueryClient } from "@tanstack/react-query";

interface PatientInvitationFormProps {
  clinicId: string;
  onSuccess?: () => void;
}

export function PatientInvitationForm({ clinicId, onSuccess }: PatientInvitationFormProps) {
  const [email, setEmail] = useState("");
  const [notes, setNotes] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !email.trim()) return;

    setIsLoading(true);
    try {
      console.log('Creating patient invitation with:', {
        p_clinic_id: clinicId,
        p_email: email.trim(),
        p_invited_by: user.id,
        p_notes: notes.trim() || null
      });

      const { data, error } = await supabase.rpc('create_patient_invitation', {
        p_clinic_id: clinicId,
        p_email: email.trim(),
        p_invited_by: user.id,
        p_notes: notes.trim() || null
      });

      if (error) {
        console.error('RPC Error:', error);
        throw error;
      }

      console.log('Invitation created successfully:', data);

      // Invalidate the patient invitations query to refresh the list
      queryClient.invalidateQueries({ queryKey: ["patient-invitations", clinicId] });

      toast({
        title: "Invitation sent",
        description: `Patient invitation sent to ${email}`,
      });

      setEmail("");
      setNotes("");
      onSuccess?.();
    } catch (error) {
      console.error('Error sending invitation:', error);
      toast({
        title: "Error",
        description: "Failed to send invitation. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <UserPlus className="w-5 h-5" />
          Invite Patient
        </CardTitle>
        <CardDescription>
          Send an invitation to a patient to join your clinic
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="email">Patient Email *</Label>
            <Input
              id="email"
              type="email"
              placeholder="patient@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          
          <div>
            <Label htmlFor="notes">Notes (Optional)</Label>
            <Textarea
              id="notes"
              placeholder="Additional information for the patient..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
            />
          </div>

          <Button type="submit" disabled={isLoading} className="w-full">
            <Mail className="w-4 h-4 mr-2" />
            {isLoading ? "Sending..." : "Send Invitation"}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
